import { DATE_FORMAT_SHORT } from 'common/data/constants';
import { StyledButtonGray, StyledField } from 'components/Common';
import { ViewCountry } from 'components/ViewCountry/ViewCountry';
import { useFormikContext } from 'formik';
import { formatDate } from 'helpers/formatDate';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import styled from 'styled-components';
import ApplicantEditForm from '../ApplicantEditForm';
import {
  isUserOperator,
  isUserEmployee,
  isUserClient,
} from 'helpers/jwt_helper';
import { useParams } from 'react-router-dom';
import { orderApplicantOrderFormDownloadRequest } from 'helpers/api-requests/admin';
import { showToastError } from 'helpers/utils/toast';
import { downloadDocument } from 'helpers/downloadDocument';
import { useOrderAlert } from 'hooks/useOrderAlert';
import { typeColor } from 'helpers/styles/typeColor';
import { ChangeStatus } from 'components/ChangeStatus/ChangeStatus';
import { CopyButton } from 'components/Common/CopyButton';
import { useSelector } from 'react-redux';
import { HelpText } from 'components/HelpText';

export const StyledLine = styled.div`
  border-left: 1px solid #dee0e7;
`;

export const StyledCol = styled(Col)`
  border-left: 1px solid #dee0e7;
`;

const ApplicantCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e8ecf1;
  padding: 20px 24px;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    border-color: #d0d7e0;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px 0 0 12px;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f0f2f5;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  flex-shrink: 0;
  min-width: 120px;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #0f172a;
  font-weight: 600;
  text-align: right;
  // word-break: break-word;
  max-width: 60%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;

  a {
    color: #3b82f6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #f0f2f5;
  }
`;

const ActionButton = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #475569;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  i {
    font-size: 14px;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #ef4444;
  border-color: #fecaca;

  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #fca5a5;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.12);
  }
`;

const InfoButton = styled(ActionButton)`
  color: #3b82f6;
  border-color: #bfdbfe;

  &:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #93c5fd;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.12);
  }
`;

const StickerButton = styled(ActionButton)`
  color: #8b5cf6;
  border-color: #ddd6fe;

  &:hover:not(:disabled) {
    background: #f5f3ff;
    border-color: #c4b5fd;
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.12);
  }
`;

const EnvelopeButton = styled(ActionButton)`
  color: #f59e0b;
  border-color: #fde68a;

  &:hover:not(:disabled) {
    background: #fffbeb;
    border-color: #fcd34d;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.12);
  }
`;

const EditButton = styled(StyledButtonGray)`
  padding: 6px 18px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-weight: 500;
  font-size: 12px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  i {
    font-size: 14px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid #f0f2f5;

  @media (max-width: 576px) {
    gap: 6px;
  }
`;

const ButtonDivider = styled.span`
  width: 1px;
  height: 24px;
  background: #e2e8f0;
  margin: 0 4px;

  @media (max-width: 576px) {
    display: none;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f2f5;
  flex-wrap: wrap;
  gap: 12px;
`;

export const convertDataToDisplay = field => (field ? field : '-');

// Helper function to render copyable values
const renderCopyable = (value, label, fallback = '-') => {
  if (!value) return fallback;
  return <CopyButton value={value} label={label} />;
};

export const ApplicantDetail = ({
  applicant,
  index,
  removeApplicant,
  orderTypeFieldsSettings,
  setApplicantCount,
  applicantData,
  onDownload,
  isDisabledFields,
  hideStatus = false,
}) => {
  const [isOpenEditApplicant, setIsOpenEditApplicant] = useState(
    !applicant.firstName,
  );
  const { id } = useParams();
  const { t } = useTranslation();
  const formik = useFormikContext();
  const { openAlert } = useOrderAlert();

  // Get countries from Redux - same as ViewCountry
  const countries = useSelector(state => state.references.countries);

  // Helper function to get country name from ID - same logic as ViewCountry
  const getCountryName = countryId => {
    if (!countryId) return null;
    const country = countries?.find(item => item.id === countryId);
    return country?.country?.name ?? country?.name ?? null;
  };

  useEffect(() => {
    if (!applicant.firstName && isOpenEditApplicant) {
      setApplicantCount(1);
    }
  }, []);

  const handleScrollToTarget = index => {
    const element = document.getElementById(`applicant_${index}`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleCloseEditApplicant = index => {
    setIsOpenEditApplicant(false);
    handleScrollToTarget(index);
  };

  const handleChangeApplicantStatus = status => {
    formik.setFieldValue(`applicants[${index}].statusApplicant`, status.value);
  };

  const handleDownload = (customer, fileType) => {
    if (onDownload) {
      onDownload(customer, fileType);
    } else {
      orderApplicantOrderFormDownloadRequest(
        id,
        customer.customerId,
        fileType,
        { responseType: 'blob' },
      )
        .then(response => {
          downloadDocument(
            response,
            `Order-${id}-${customer.applicantDetail.firstName} ${customer.applicantDetail.lastName}-${fileType}.pdf`,
          );
        })
        .catch(err => {
          showToastError(err.message);
        });
    }
  };

  const handleDelete = () => {
    removeApplicant();
    openAlert({
      message: t('order.deleteApplicantNotification'),
      type: typeColor.danger,
    });
  };

  const currentStatus = applicantData?.statusApplicant || 'draft';

  // Format dates for display
  const formatDateDisplay = date => {
    return date ? formatDate(date, DATE_FORMAT_SHORT) : null;
  };

  // const isSimplifiedMode = applicant?.isSimplifiedMode;
  const isSimplifiedMode = applicant?.isSimplifiedMode;

  return (
    <StyledField className="p-0 w-100">
      {isOpenEditApplicant ? (
        <ApplicantEditForm
          removeApplicant={removeApplicant}
          applicant={applicant}
          index={index}
          handleClose={() => handleCloseEditApplicant(index)}
          orderTypeFieldsSettings={orderTypeFieldsSettings}
        />
      ) : (
        <>
          <div id={`applicant_${index}`}></div>

          <ApplicantCard>
            {/* Header with Status */}
            <CardHeader>
              {!hideStatus && (
                <ChangeStatus
                  disabled={
                    isDisabledFields ||
                    isUserClient ||
                    isUserEmployee ||
                    isSimplifiedMode
                  }
                  value={currentStatus}
                  onChange={handleChangeApplicantStatus}
                  className="change-status"
                />
              )}

              {isUserOperator && isSimplifiedMode && (
                <HelpText
                  variant="warning"
                  size="sm"
                  showLeftBorder
                  leftBorderColor="#f59e0b"
                  marginTop="4px"
                  style={{ width: '100%' }}
                  icon="bx bx-lock-alt"
                >
                  <span>
                    {t('order.statusLockedInSimplifiedMode', {
                      defaultValue:
                        'To change status: disable simplified mode.',
                    })}
                  </span>
                </HelpText>
              )}
            </CardHeader>

            {/* Body with Info Rows - Left and Right Columns */}
            <Row>
              {/* Left Column - Personal Information */}
              <Col xs={12} md={12} lg={6}>
                <SectionTitle>
                  <span>👤</span> {t('order.personalInformation')}
                </SectionTitle>
                <InfoContainer>
                  <InfoRow>
                    <InfoLabel>{t('auth.firstName')}</InfoLabel>
                    <InfoValue>
                      {renderCopyable(applicant.firstName, 'First Name')}
                    </InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>{t('auth.lastName')}</InfoLabel>
                    <InfoValue>
                      {renderCopyable(applicant.lastName, 'Last Name')}
                    </InfoValue>
                  </InfoRow>
                  {applicant.additionalName && (
                    <InfoRow>
                      <InfoLabel>{t('auth.additionalName')}</InfoLabel>
                      <InfoValue>
                        {renderCopyable(
                          applicant.additionalName,
                          'Additional Name',
                        )}
                      </InfoValue>
                    </InfoRow>
                  )}
                  {orderTypeFieldsSettings?.enableApplicantBirthdate && (
                    <InfoRow>
                      <InfoLabel>{t('auth.birthday')}</InfoLabel>
                      <InfoValue>
                        {renderCopyable(
                          formatDateDisplay(applicant.birthday),
                          'Birthday',
                        )}
                      </InfoValue>
                    </InfoRow>
                  )}
                  {applicant.sex && (
                    <InfoRow>
                      <InfoLabel>{t('common.gender')}</InfoLabel>
                      <InfoValue>
                        {renderCopyable(applicant.sex, 'Gender')}
                      </InfoValue>
                    </InfoRow>
                  )}
                </InfoContainer>
              </Col>

              {/* Right Column - Passport or Contact Information */}
              <Col xs={12} md={12} lg={6}>
                <SectionTitle>
                  {orderTypeFieldsSettings?.enableApplicantPassport
                    ? '🛂 '
                    : '📞 '}
                  {orderTypeFieldsSettings?.enableApplicantPassport
                    ? t('passport.information')
                    : t('common.contactInfo')}
                </SectionTitle>
                <InfoContainer>
                  {orderTypeFieldsSettings?.enableApplicantPassport ? (
                    <>
                      <InfoRow>
                        <InfoLabel>{t('common.citizenship')}</InfoLabel>
                        <InfoValue>
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <ViewCountry id={applicant.citizenship} />
                            {applicant.citizenship && (
                              <CopyButton
                                value={getCountryName(applicant.citizenship)}
                                label="Citizenship"
                                showValue={false}
                              />
                            )}
                          </span>
                        </InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>{t('passport.number')}</InfoLabel>
                        <InfoValue>
                          {renderCopyable(
                            applicant.passport?.number,
                            'Passport Number',
                          )}
                        </InfoValue>
                      </InfoRow>
                      {applicant.passport?.startDate && (
                        <InfoRow>
                          <InfoLabel>{t('passport.startDate')}</InfoLabel>
                          <InfoValue>
                            {renderCopyable(
                              formatDateDisplay(applicant.passport.startDate),
                              'Passport Start Date',
                            )}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {applicant.passport?.expirationDate && (
                        <InfoRow>
                          <InfoLabel>{t('passport.expirationDate')}</InfoLabel>
                          <InfoValue>
                            {renderCopyable(
                              formatDateDisplay(
                                applicant.passport.expirationDate,
                              ),
                              'Passport Expiration Date',
                            )}
                          </InfoValue>
                        </InfoRow>
                      )}
                    </>
                  ) : (
                    <>
                      <InfoRow>
                        <InfoLabel>{t('common.phone')}</InfoLabel>
                        <InfoValue>
                          <a href={`tel:${applicant.phone}`}>
                            {renderCopyable(applicant.phone, 'Phone Number')}
                          </a>
                        </InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>{t('auth.email')}</InfoLabel>
                        <InfoValue>
                          <a href={`mailto:${applicant.email}`}>
                            {renderCopyable(applicant.email, 'Email')}
                          </a>
                        </InfoValue>
                      </InfoRow>
                    </>
                  )}
                </InfoContainer>
              </Col>
            </Row>

            {/* ALL Action Buttons */}
            <ButtonGroup>
              <EditButton
                disabled={isDisabledFields}
                onClick={() => setIsOpenEditApplicant(true)}
              >
                <i className="bx bxs-edit-alt" />
                {t('common.edit')}
              </EditButton>

              {isUserOperator && applicantData?.customerId && id && (
                <>
                  <ButtonDivider />
                  <InfoButton
                    disabled={isDisabledFields}
                    onClick={e => {
                      e.stopPropagation();
                      handleDownload(applicantData, 'info');
                    }}
                  >
                    <i className="bx bx-download" />
                    info
                  </InfoButton>
                  <StickerButton
                    disabled={isDisabledFields}
                    onClick={e => {
                      e.stopPropagation();
                      handleDownload(applicantData, 'sticker');
                    }}
                  >
                    <i className="bx bx-download" />
                    sticker
                  </StickerButton>
                  <EnvelopeButton
                    disabled={isDisabledFields}
                    onClick={e => {
                      e.stopPropagation();
                      handleDownload(applicantData, 'envelope');
                    }}
                  >
                    <i className="bx bx-download" />
                    envelope
                  </EnvelopeButton>
                </>
              )}

              {!isUserEmployee && (
                <>
                  <ButtonDivider />
                  <DeleteButton
                    disabled={isDisabledFields}
                    onClick={e => {
                      e.stopPropagation();
                      handleDelete();
                    }}
                  >
                    <i className="bx bxs-trash" />
                    {t('common.delete')}
                  </DeleteButton>
                </>
              )}
            </ButtonGroup>
          </ApplicantCard>
        </>
      )}
    </StyledField>
  );
};
